// src/components/DesktopSidebar.jsx
// Persistent navigation rail shown only on md+ screens. Mobile keeps BottomNav.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveUploadUrl } from '../api';
import {
  LayoutDashboard,
  Users,
  Bookmark,
  CandlestickChart,
  Newspaper,
  Settings,
  ShieldCheck,
  LogOut,
} from 'lucide-react';

const items = [
  { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { key: 'community', icon: Users, label: 'Community', path: '/dashboard/community' },
  { key: 'watchlist', icon: Bookmark, label: 'Watchlist', path: '/dashboard/watchlist' },
  { key: 'markets', icon: CandlestickChart, label: 'Markets', path: '/dashboard/markets', requiresMarket: true },
  { key: 'news', icon: Newspaper, label: 'News', path: '/dashboard/news' },
  { key: 'settings', icon: Settings, label: 'Settings', path: '/dashboard/settings' },
];

const DesktopSidebar = ({
  marketAccess = false,
  isAdmin = false,
  currentPath = '',
  unreadNotifications = 0,
  onLogout,
  user = null,
}) => {
  const navigate = useNavigate();

  const isActive = key => {
    if (key === 'dashboard') return currentPath === '/dashboard';
    if (key === 'community') {
      return currentPath.includes('/dashboard/community') || currentPath.includes('/dashboard/profile/');
    }
    return currentPath.includes(`/dashboard/${key}`);
  };

  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-slate-200 bg-white md:flex dark:border-slate-800 dark:bg-slate-900">
      {/* Brand */}
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-3 border-b border-slate-200 px-4 py-4 text-left dark:border-slate-800"
      >
        <img
          src="/logo-light.png"
          alt="Edgehub logo"
          className="h-9 w-9 shrink-0 object-contain dark:hidden"
        />
        <img
          src="/logo.png?v=2"
          alt="Edgehub logo"
          className="hidden h-9 w-9 shrink-0 object-contain dark:block"
        />
        <span>
          <span className="block text-sm font-bold tracking-tight text-slate-900 dark:text-white">Edgehub</span>
          <span className="block text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
            Trading workspace
          </span>
        </span>
      </button>

      {/* Primary navigation */}
      <nav className="flex-1 space-y-1 overflow-y-auto p-2.5">
        {items
          .filter(item => !item.requiresMarket || marketAccess)
          .map(({ key, icon: Icon, label, path }) => {
            const active = isActive(key);
            return (
              <button
                key={key}
                onClick={() => navigate(path)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                <Icon size={18} />
                <span>{label}</span>
                {key === 'dashboard' && unreadNotifications > 0 ? (
                  <span className="ml-auto rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                ) : null}
              </button>
            );
          })}

        {isAdmin ? (
          <button
            onClick={() => navigate('/dashboard/admin')}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              currentPath.includes('/dashboard/admin')
                ? 'bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
            }`}
          >
            <ShieldCheck size={18} />
            <span>Admin panel</span>
          </button>
        ) : null}
      </nav>

      {/* Footer */}
      <div className="border-t border-slate-200 p-3 dark:border-slate-800">
        <button
          onClick={() => navigate('/dashboard/settings/profile')}
          className="mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
          title="Profile settings"
        >
          {user?.avatar ? (
            <img src={resolveUploadUrl(user.avatar)} alt={user?.name || 'Profile'} className="h-8 w-8 shrink-0 rounded-full object-cover" />
          ) : (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-xs font-bold text-white">
              {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
            </span>
          )}
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">{user?.name || 'Trader'}</span>
            <span className="block truncate text-[10px] text-slate-400 dark:text-slate-500">{user?.email || ''}</span>
          </span>
        </button>
        <button
          onClick={onLogout}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-500/10 dark:hover:text-red-400"
        >
          <LogOut size={18} />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
};

export default DesktopSidebar;
